import type { FieldPolicy, Reference, StoreObject, TypePolicies } from '@apollo/client';
import { uniqBy } from 'lodash';
import { mergeDocumentStatusJobBatches } from 'src/components/reports/report-detail/document-status-jobs';
import type { Apollo } from 'src/gql/generated';

const replaceExistingWithIncoming: FieldPolicy = {
  merge: (_existing, incoming) => incoming,
};

type OpportunitiesKeyArg = keyof Apollo.GetIntegrationOpportunitiesQueryVariables;
type DocReportRowsKeyArg = Exclude<
  keyof Apollo.GetDocumentReportRowsQueryVariables,
  'cursor' | 'offset' | 'limit' | 'reportId'
>;
const docReportRowsKeyArgsObject: Record<DocReportRowsKeyArg, true> = {
  docVariant: true,
  filterModel: true,
  filterTimeZone: true,
  listFilter: true,
  missingMatchesOnly: true,
  parentId: true,
  sortModel: true,
  trashed: true,
  unconfirmedRelationshipsOnly: true,
};

type ReportRowsKeyArg = Exclude<
  keyof Apollo.GetReportRowsQueryVariables,
  'cursor' | 'offset' | 'limit' | 'reportId'
>;
const reportRowsKeyArgsObject: Record<ReportRowsKeyArg, true> = {
  displayMode: true,
  docVariant: true,
  filterModel: true,
  filterTimeZone: true,
  groupFieldIds: true,
  groupKeys: true,
  listFilter: true,
  missingMatchesOnly: true,
  parentTreeRowId: true,
  sortModel: true,
  trashed: true,
  unconfirmedRelationshipsOnly: true,
};

type WorkflowsKeyArg = Exclude<keyof Apollo.GetWorkflowsQueryVariables, 'cursor' | 'limit'>;
const workflowsKeyArgsObject: Record<WorkflowsKeyArg, true> = {
  filterModel: true,
  grain: true,
  groupFields: true,
  groupKeys: true,
  listFilter: true,
  sortModel: true,
  workflowFilter: true,
};

type PartyRowsKeyArg = Exclude<keyof Apollo.GetPartyRowsQueryVariables, 'cursor' | 'limit'>;
const partyRowsKeyArgsObject: Record<PartyRowsKeyArg, true> = {
  filterModel: true,
  sortModel: true,
};
const opportunitiesQuery: keyof Apollo.Query = 'integrationOpportunities';
const documentStatusJobsQuery: keyof Apollo.Query = 'documentStatusJobs';
const reviewTypename: Apollo.ExtractionQuestionReview['__typename'] = 'ExtractionQuestionReview';
const docFileTypename: Apollo.DocFile['__typename'] = 'DocFile';
const signatureDetectionTypename: Apollo.SignatureDetection['__typename'] = 'SignatureDetection';

export const typePolicies: TypePolicies = {
  AgentThread: {
    fields: {
      messages: {
        merge: (existing = [], incoming = []) => {
          // Order matters here b/c uniqBy takes the first item in the array.
          return uniqBy([...incoming, ...existing], ref => ref.__ref);
        },
      },
    } satisfies Partial<Record<keyof Apollo.AgentThread, FieldPolicy>>,
  },
  ApiDeckOpportunityWithRelations: {
    /**
     * Keep opportunities embedded under their `Query.apiDeckOpportunity(opportunityId, serviceId)`
     * field instead of normalizing by `id` alone. The opportunityId is not unique across CRMs, so
     * a shared normalized entity would let one provider's in-flight `fields` overwrite another's.
     */
    keyFields: false,
  },
  BulkExport: {
    fields: {
      // Replace the parts array wholesale on every write — each subscription
      // event publishes the authoritative parts list. The default merge
      // strategy would normalize sub-entries by index, but BulkExportPart has
      // no id and that produces cross-event collisions.
      parts: { merge: (_existing, incoming) => incoming },
      // Same rationale for skippedDocuments. The activity writes the full
      // list each time; cumulative merging would create duplicates.
      skippedDocuments: { merge: (_existing, incoming) => incoming },
    } satisfies Partial<Record<keyof Apollo.BulkExport, FieldPolicy>>,
  },
  // BulkExportPart has no id field — keep its data inline on the parent
  // BulkExport so Apollo doesn't try to normalize it into its own entity
  // and conflate parts across different exports.
  BulkExportPart: {
    keyFields: false,
  },
  BulkExportSkippedDocument: {
    keyFields: false,
  },
  Document: {
    fields: {
      /**
       * Dedupe by cache pointer (`DocumentRelationship:<id>`), not `readField('id', ref)`.
       * During multi-ref writes, `readField('id')` can be undefined briefly; Ramda uniqBy then
       * treats every such ref as the same key and keeps only one edge—dropping real relationships.
       */
      childRelationships: {
        merge: (existing = [], incoming = []) =>
          uniqBy([...incoming, ...existing], ref => ref.__ref),
      },
      parentRelationships: {
        merge: (existing = [], incoming = []) =>
          uniqBy([...incoming, ...existing], ref => ref.__ref),
      },
      questionJobs: {
        merge: (existing = [], incoming = []) =>
          uniqBy([...incoming, ...existing], ref => ref.__ref),
      },
    },
  },
  DocumentStatusJob: {
    fields: {
      batches: {
        merge: (
          existing: Apollo.DocumentStatusJobBatch[] = [],
          incoming: Apollo.DocumentStatusJobBatch[] = []
        ) => mergeDocumentStatusJobBatches(existing, incoming),
      },
    } satisfies Partial<Record<keyof Apollo.DocumentStatusJob, FieldPolicy>>,
  },
  /**
   * Toolkit rows have no `id`. Key them by slug so a grant mutation's
   * optimistic payload updates the same entity the catalogue query is watching.
   */
  ExternalToolkit: { keyFields: ['slug'] },
  PostSignatureStep: {
    fields: { actions: replaceExistingWithIncoming },
  },
  PreSignatureStep: {
    fields: { actions: replaceExistingWithIncoming },
  },
  Query: {
    fields: {
      /**
       * A refetch is the truth about which jobs exist, so a job that finished
       * while this tab missed its last event is not left running. Visibility can
       * lag a job that just started; its next progress event lists it again.
       */
      [documentStatusJobsQuery]: { merge: false },
      [opportunitiesQuery]: {
        keyArgs: ['filter', 'serviceId'] satisfies OpportunitiesKeyArg[],
        merge: (
          existing: StoreObject | undefined,
          incoming: StoreObject | undefined,
          { readField, variables }
        ) => {
          const nextCursorKey: keyof Apollo.ApiDeckOpportunitiesResponse = 'nextCursor';
          const cursorKey: keyof Apollo.GetIntegrationOpportunitiesQueryVariables = 'cursor';
          const opportunitiesKey: keyof Apollo.ApiDeckOpportunitiesResponse = 'opportunities';
          const existingOpportunities = existing
            ? (readField<Reference[]>(opportunitiesKey, existing) ?? [])
            : [];
          const incomingOpportunities = incoming
            ? (readField<Reference[]>(opportunitiesKey, incoming) ?? [])
            : [];
          const hasCursor = Boolean(variables?.[cursorKey]);

          return {
            __typename: 'ApiDeckOpportunitiesResponse',
            [nextCursorKey]: incoming?.nextCursor ?? null,
            // Replaces opps in cache if no cursor.
            [opportunitiesKey]: hasCursor
              ? uniqBy([...existingOpportunities, ...incomingOpportunities], ref => ref.__ref)
              : incomingOpportunities,
          } satisfies Record<keyof Apollo.ApiDeckOpportunitiesResponse, unknown>;
        },
      },
      partyRows: {
        /**
         * keyArgs ensure that when filterModel/sortModel changes, Apollo cache stores results in a
         * unique place. Conversely, when limit/cursor changes, results merge into the same array
         * for pagination.
         */
        keyArgs: Object.keys(partyRowsKeyArgsObject),
        merge: (
          existing: StoreObject | undefined,
          incoming: StoreObject | undefined,
          { readField, variables }
        ) => {
          const rowsKey: keyof Apollo.PartyRowsInfo = 'rows';
          const metaKey: keyof Apollo.PartyRowsInfo = 'meta';
          const cursorKey: keyof Apollo.GetPartyRowsQueryVariables = 'cursor';
          const existingRows = existing ? (readField<Reference[]>(rowsKey, existing) ?? []) : [];
          const incomingRows = incoming ? (readField<Reference[]>(rowsKey, incoming) ?? []) : [];
          const cursor = variables?.[cursorKey];

          const getMergedRows = (): readonly Reference[] => {
            if (!cursor) return incomingRows;

            const cursorIndex = existingRows.findIndex(ref => readField('id', ref) === cursor);
            if (cursorIndex === -1)
              return uniqBy([...existingRows, ...incomingRows], ref => ref.__ref);

            return uniqBy(
              [
                ...existingRows.slice(0, cursorIndex + 1),
                ...incomingRows,
                ...existingRows.slice(cursorIndex + 1),
              ],
              ref => ref.__ref
            );
          };

          return {
            __typename: 'PartyRowsInfo',
            [metaKey]: incoming?.[metaKey] ?? existing?.[metaKey] ?? {},
            [rowsKey]: getMergedRows(),
          } satisfies Record<keyof Apollo.PartyRowsInfo, unknown>;
        },
      },
      workflows: {
        /**
         * keyArgs ensure that when filterModel/sortModel/workflowFilter changes, Apollo cache stores results in a unique place.
         * Conversely, when limit/cursor changes, Apollo cache stores in the same place/array for pagination.
         */
        keyArgs: Object.keys(workflowsKeyArgsObject),
        merge: (
          existing: StoreObject | undefined,
          incoming: StoreObject | undefined,
          { readField, variables }
        ) => {
          const rowsKey: keyof Apollo.WorkflowRowsInfo = 'rows';
          const metaKey: keyof Apollo.WorkflowRowsInfo = 'meta';
          const aggregatesKey: keyof Apollo.WorkflowRowsInfo = 'aggregates';
          const groupCountKey: keyof Apollo.WorkflowRowsInfo = 'groupCount';
          const groupHeadersKey: keyof Apollo.WorkflowRowsInfo = 'groupHeaders';
          const cursorKey: keyof Apollo.GetWorkflowsQueryVariables = 'cursor';
          const existingRows = existing ? (readField<Reference[]>(rowsKey, existing) ?? []) : [];
          const incomingRows = incoming ? (readField<Reference[]>(rowsKey, incoming) ?? []) : [];
          const cursor = variables?.[cursorKey];

          const getMergedRows = (): readonly Reference[] => {
            if (!cursor) return incomingRows;

            const cursorIndex = existingRows.findIndex(ref => readField('id', ref) === cursor);
            if (cursorIndex === -1)
              return uniqBy([...existingRows, ...incomingRows], ref => ref.__ref);

            return uniqBy(
              [
                ...existingRows.slice(0, cursorIndex + 1),
                ...incomingRows,
                ...existingRows.slice(cursorIndex + 1),
              ],
              ref => ref.__ref
            );
          };

          return {
            __typename: 'WorkflowRowsInfo',
            [aggregatesKey]: incoming?.[aggregatesKey] ?? existing?.[aggregatesKey] ?? null,
            [groupCountKey]: incoming?.[groupCountKey] ?? existing?.[groupCountKey] ?? null,
            [groupHeadersKey]: incoming?.[groupHeadersKey] ?? existing?.[groupHeadersKey] ?? [],
            [metaKey]: incoming?.[metaKey] ?? existing?.[metaKey] ?? {},
            [rowsKey]: getMergedRows(),
          } satisfies Record<keyof Apollo.WorkflowRowsInfo, unknown>;
        },
      },
    },
  },
  Reminder: {
    fields: { assignees: replaceExistingWithIncoming },
  },
  Report: {
    fields: {
      columns: replaceExistingWithIncoming,
      reportRows: {
        keyArgs: Object.keys(reportRowsKeyArgsObject),
        merge: (
          existing: StoreObject | undefined,
          incoming: StoreObject | undefined,
          { readField }
        ) => {
          const rowsKey: keyof Apollo.ReportRowsInfo = 'rows';
          const totalCountKey: keyof Apollo.ReportRowsInfo = 'totalCount';
          const groupCountKey: keyof Apollo.ReportRowsInfo = 'groupCount';
          const existingRows = existing ? (readField<Reference[]>(rowsKey, existing) ?? []) : [];
          const incomingRows = incoming ? (readField<Reference[]>(rowsKey, incoming) ?? []) : [];

          return {
            __typename: 'ReportRowsInfo',
            [groupCountKey]: incoming?.[groupCountKey] ?? existing?.[groupCountKey] ?? null,
            [rowsKey]: uniqBy([...existingRows, ...incomingRows], ref => ref.__ref),
            [totalCountKey]: incoming?.[totalCountKey] ?? existing?.[totalCountKey] ?? 0,
          } satisfies Record<keyof Apollo.ReportRowsInfo, unknown>;
        },
      },
      rows: {
        keyArgs: Object.keys(docReportRowsKeyArgsObject),
        merge: (
          existing: StoreObject | undefined,
          incoming: StoreObject | undefined,
          { readField }
        ) => {
          const docsKey: keyof Apollo.RowsInfo = 'docs';
          const totalCountKey: keyof Apollo.RowsInfo = 'totalCount';
          const existingDocs = existing ? (readField<Reference[]>(docsKey, existing) ?? []) : [];
          const incomingDocs = incoming ? (readField<Reference[]>(docsKey, incoming) ?? []) : [];

          return {
            __typename: 'RowsInfo',
            [docsKey]: uniqBy([...existingDocs, ...incomingDocs], ref => ref.__ref),
            [totalCountKey]: incoming?.[totalCountKey] ?? existing?.[totalCountKey] ?? 0,
          } satisfies Record<keyof Apollo.RowsInfo, unknown>;
        },
      },
    },
  },
  SignatureStep: {
    fields: { actions: replaceExistingWithIncoming },
  },
  Subscription: {
    fields: {
      onAgentEvent: replaceExistingWithIncoming,
    },
  },
  User: {
    fields: {
      myRecentDocuments: {
        merge: (existing = [], incoming = []) =>
          uniqBy([...incoming, ...existing], ref => ref.__ref),
      },
    } satisfies Partial<Record<keyof Apollo.User, FieldPolicy>>,
  },
  Workflow: {
    fields: { phases: replaceExistingWithIncoming },
  },
  /**
   * Chart series datasets are scoped to the query that requested them and their `id`
   * is the Dimension / Measure name (`count`, `createdAt`, …). Normalizing them would
   * give every Chart on the page one shared cache entry, so the last Chart to load
   * would overwrite the data of every other Chart using the same Measure.
   */
  WorkflowChartCategory: { keyFields: false },
  WorkflowChartDimensionSeries: { keyFields: false },
  WorkflowChartTotalSeries: { keyFields: false },
  WorkflowChartValueSeries: { keyFields: false },
  [reviewTypename]: { merge: true },
  /**
   * Several operations select a file version's `fileLocation { mimetype }` without its `id`.
   * Without a merge, that write replaces the normalized DocFile reference with an embedded
   * object, every query reading the file's other fields goes incomplete, and Apollo refetches
   * them with `loading: true` (the Editor ribbon and Document Summary panel blank out).
   */
  [docFileTypename]: { merge: true },
  /**
   * SignatureDetection has no `id`, and the Repository row fragment selects fewer of its
   * fields than the Document Summary panel. Merging keeps the panel's fields when a row
   * query or the documentsUpdated subscription writes the same file version.
   */
  [signatureDetectionTypename]: { merge: true },
};
